/**
 * Riders Corner Container Component
 *
 * Container component with business logic for riders corner page
 * Follows Single Responsibility Principle: Handles all business logic and data fetching
 * Dependency Inversion: Depends on hooks and services (abstractions)
 */

import React, { useCallback } from 'react';
import { useRouter } from 'next/router';
import { RidersCornerPresenter } from './RidersCornerPresenter';
import { useRidersPaginated, Rider } from '@/hooks/useRidersPaginated';
import { RidersCornerListItem } from '@/services/ridersCornerService';
import { normalizeCategory } from '@/utils/ridersCornerMapper';

// Map Rider type to RidersCornerListItem type
const mapRiderToListItem = (rider: Rider): RidersCornerListItem => {
  return {
    _id: rider._id,
    title: rider.title || rider.shortTitle || rider.name,
    shortTitle: rider.shortTitle,
    slug: rider.slug,
    category: normalizeCategory(rider.category),
    imageUrl: rider.imageUrl || [],
    totalDistance: rider.totalDistance,
    totalHoursFrom: rider.totalHoursFrom,
    totalHoursTo: rider.totalHoursTo,
    description: rider.description || '',
    shortDescription: rider.shortDescription || rider.description || '',
    isActive: true,
    isDeleted: false,
  };
};

export const RidersCornerContainer: React.FC = () => {
  const router = useRouter();
  // Use paginated hook for traditional pagination
  const {
    riders,
    isLoading,
    error,
    search,
    setSearch,
    currentPage,
    totalPages,
    setPage,
  } = useRidersPaginated();

  // Map riders to the expected format
  const mappedRiders: RidersCornerListItem[] = riders.map(mapRiderToListItem);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
    },
    [setSearch]
  );

  const handleSearchSubmit = useCallback(
    (value: string) => {
      setSearch(value);
    },
    [setSearch]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const handleJoinCommunity = useCallback((email: string) => {
    // TODO: Implement community join functionality
    console.log('Joining community with email:', email);
    // You can add API call here or use a service
  }, []);

  const handleNewChat = useCallback(() => {
    router.push('/chat?new=1');
  }, [router]);

  const handleLogoClick = useCallback(() => {
    // Navigate to chat to show full landing page
    router.push('/chat?view=full-landing');
  }, [router]);

  return (
    <RidersCornerPresenter
      searchQuery={search}
      onSearchChange={handleSearchChange}
      onSearchSubmit={handleSearchSubmit}
      riders={mappedRiders}
      isLoading={isLoading}
      error={error}
      hasSearchQuery={search.trim().length > 0}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={handlePageChange}
      onJoinCommunity={handleJoinCommunity}
      onNewChat={handleNewChat}
      onLogoClick={handleLogoClick}
    />
  );
};
